import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2, TrendingUp, Users, Home, Clock, BarChart3 } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

interface AnalyticsData {
  totalClients: number;
  totalProperties: number;
  totalPlacements: number;
  activePlacements: number;
  matchingSuccessRate: number;
  averagePlacementDuration: number;
  propertyPopularity: Array<{ name: string; matches: number; status: string }>;
  placementTrends: Array<{ month: string; placements: number }>;
  clientStatus: Array<{ name: string; value: number }>;
  propertyStatus: Array<{ name: string; value: number }>;
}

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

const PlacementAnalytics = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalClients: 0,
    totalProperties: 0,
    totalPlacements: 0,
    activePlacements: 0,
    matchingSuccessRate: 0,
    averagePlacementDuration: 0,
    propertyPopularity: [],
    placementTrends: [],
    clientStatus: [],
    propertyStatus: [],
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchAnalytics();
    }
  }, [isAdmin]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }

      setUser(session.user);

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roleData) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
        return;
      }

      setIsAdmin(true);
    } catch (error: any) {
      console.error("Error checking auth:", error);
      toast.error("Authentication error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      // Fetch counts
      const { count: clientCount } = await supabase
        .from("client_profiles")
        .select("*", { count: "exact", head: true });

      const { count: propertyCount } = await supabase
        .from("properties")
        .select("*", { count: "exact", head: true });

      const { data: placements } = await supabase
        .from("placements")
        .select("*");

      const { count: activePlacementCount } = await supabase
        .from("placements")
        .select("*", { count: "exact", head: true })
        .eq("status", "active");

      // Calculate matching success rate
      const clientsWithPlacements = new Set(placements?.map(p => p.client_id) || []).size;
      const matchingSuccessRate = clientCount ? (clientsWithPlacements / clientCount) * 100 : 0;

      // Calculate average placement duration
      const completedPlacements = placements?.filter(p => p.status === "completed" && p.end_date) || [];
      const durations = completedPlacements.map(p => {
        const start = new Date(p.start_date);
        const end = new Date(p.end_date!);
        return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      });
      const averageDuration = durations.length > 0 
        ? durations.reduce((a, b) => a + b, 0) / durations.length 
        : 0;

      // Property popularity
      const { data: properties } = await supabase
        .from("properties")
        .select("*");

      const propertyMatchCounts = properties?.map(prop => {
        const matchCount = placements?.filter(p => p.property_id === prop.id).length || 0;
        return {
          name: prop.name,
          matches: matchCount,
          status: prop.status,
        };
      }).sort((a, b) => b.matches - a.matches).slice(0, 5) || [];

      // Placement trends (last 6 months)
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthName = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        
        const count = placements?.filter(p => {
          const placementDate = new Date(p.created_at);
          return placementDate >= monthStart && placementDate <= monthEnd;
        }).length || 0;

        months.push({ month: monthName, placements: count });
      }

      // Client status distribution
      const clientStatuses = [
        { name: "With Placement", value: clientsWithPlacements },
        { name: "No Placement", value: (clientCount || 0) - clientsWithPlacements },
      ];

      // Property status distribution
      const availableCount = properties?.filter(p => p.status === "available").length || 0;
      const occupiedCount = properties?.filter(p => p.status === "occupied").length || 0;
      const maintenanceCount = properties?.filter(p => p.status === "maintenance").length || 0;

      const propertyStatuses = [
        { name: "Available", value: availableCount },
        { name: "Occupied", value: occupiedCount },
        { name: "Maintenance", value: maintenanceCount },
      ].filter(item => item.value > 0);

      setAnalytics({
        totalClients: clientCount || 0,
        totalProperties: propertyCount || 0,
        totalPlacements: placements?.length || 0,
        activePlacements: activePlacementCount || 0,
        matchingSuccessRate,
        averagePlacementDuration: averageDuration,
        propertyPopularity: propertyMatchCounts,
        placementTrends: months,
        clientStatus: clientStatuses,
        propertyStatus: propertyStatuses,
      });
    } catch (error: any) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="max-w-7xl mx-auto space-y-8">
          <div>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <BarChart3 className="h-8 w-8" />
              Placement Analytics
            </h1>
            <p className="text-muted-foreground mt-2">
              Track matching success, placement metrics, and property performance
            </p>
          </div>

          {/* Key Metrics */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Clients</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.totalClients}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.activePlacements} with active placements
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
                <Home className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.totalProperties}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Across all landlords
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Matching Success Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.matchingSuccessRate.toFixed(1)}%</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Clients successfully placed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Placement Duration</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{Math.round(analytics.averagePlacementDuration)} days</div>
                <p className="text-xs text-muted-foreground mt-1">
                  For completed placements
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 1 */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Placement Trends */}
            <Card>
              <CardHeader>
                <CardTitle>Placement Trends</CardTitle>
                <CardDescription>New placements over the last 6 months</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={analytics.placementTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="placements" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      name="Placements"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Property Popularity */}
            <Card>
              <CardHeader>
                <CardTitle>Property Popularity</CardTitle>
                <CardDescription>Top 5 most matched properties</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={analytics.propertyPopularity}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="matches" fill="hsl(var(--primary))" name="Matches" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Client Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Client Placement Status</CardTitle>
                <CardDescription>Distribution of clients by placement status</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.clientStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {analytics.clientStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Property Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Property Status</CardTitle>
                <CardDescription>Current status of all properties</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={analytics.propertyStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {analytics.propertyStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Property Details Table */}
          <Card>
            <CardHeader>
              <CardTitle>Property Performance Details</CardTitle>
              <CardDescription>Detailed breakdown of top performing properties</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analytics.propertyPopularity.map((property, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-2xl font-bold text-muted-foreground">
                        #{index + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold">{property.name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {property.matches} placement{property.matches !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                    <Badge variant={property.status === "available" ? "default" : "secondary"}>
                      {property.status}
                    </Badge>
                  </div>
                ))}
                {analytics.propertyPopularity.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    No property data available yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PlacementAnalytics;
